<script setup lang="ts">
import type { IValue } from "~/interfaces";
import { useVuelidate } from "@vuelidate/core";
import creditFormRules from "~/rules/credit";
import Validator from "~/utils/validator";
import SelectNew from "~/components/inputs/SelectNew.vue";

const { ctaText, ctaSubtext, value } = defineProps({
  ctaText: { type: String, required: true },
  ctaSubtext: { type: String, required: false, default: "" },
  values: { type: Array as () => IValue[], default: () => [], required: true },
  value: { type: Number, required: false },
});

defineComponent({
  name: "CreditAlt",
});

const emit = defineEmits(["submit"]);

const credit = useState<number>("selectedCredit");

const v$ = useVuelidate(creditFormRules, {
  credit,
});

const validate = () => {
  v$.value.$touch();
  v$.value.$validate();
  return !v$.value.$error;
};

const submit = (e: Event) => {
  e.stopPropagation();
  e.preventDefault();

  if (validate()) {
    emit("submit");
  }
};
</script>

<template>
  <!-- Section amount -->
  <section class="section-amount bg-gray-5 py-10 ">
    <div class="container mx-auto px-2 sm:px-10 text-center">
      <h2 class="section-amount-title font-semibold text-3xl text-white leading-tight px-8 mb-4 sm:px-0 md:text-4xl">
        <slot name="title"></slot>
      </h2>
      <p class="section-amount-text font-light text-lg text-white mb-5 lg:text-xl">
        <slot name="text"></slot>
      </p>
      <form class="mx-4" @submit.prevent="submit">
        <div class="max-w-md mx-auto flex flex-col">
          <SelectNew
            v-model="credit"
            class="w-full py-2 leading-normal rounded-full h-12 text-lg focus-within:shadow-white"
            disable-zero-value
            :values="values"
            test-id="selectedCreditAlt"
            :error="v$.credit.$error"
            :error-message="Validator.message(v$.credit)"
            error-margin
          />
          <div class="lg:mt-1">
            <button
              type="submit"
              class="home-submit"
              data-testid="creditAltSubmit"
            >
              <span
                class="section-amount-cta-text block font-bold text-xl leading-5"
                >{{ ctaText }}</span
              >
              <span
                v-if="ctaSubtext"
                class="section-amount-cta-subtext block leading-5 text-base"
                >{{ ctaSubtext }}</span
              >
            </button>
          </div>
        </div>
      </form>
      <h2
        class="section-amount-helper-text mt-8 text-lg text-gray-5 font-normal"
      >
        <slot name="helper_text"></slot>
      </h2>
    </div>
  </section>
</template>

<style scoped>
.home-submit {
  @apply w-full sm:px-16 py-2 mt-4 font-semibold text-xl shadow-2xl;
}
@screen sm {
  .home-submit {
    @apply inline-block w-auto py-3;
  }
}
</style>
